// ─────────────────────────────────────────────────────────────────────────────
// Request Template Studio — schema helpers
//
// Mirrors src/pages/Home/NoteType/studio/schema.js exactly (same builder
// architecture, same section/field model shape) so the two Studios behave
// identically to build in. Sections, `span` and field-level conditional
// rules all persist, so the New Request drawer reproduces the form the
// author built here card for card and field for field.
//
// field = { id, type, label, placeholder, help, span, required, options[],
//           conditional, severityOptions, yesLabel, noLabel, allowMultiple,
//           activeOnly, calc, carePlanScope, notifyRoles }
// ─────────────────────────────────────────────────────────────────────────────

export const TYPE_META = {
  Text: { ic: "text", label: "Text Field" },
  TextArea: { ic: "para", label: "Text Area" },
  Number: { ic: "num", label: "Number" },
  Date: { ic: "date", label: "Date" },
  Time: { ic: "time", label: "Time" },
  Dropdown: { ic: "drop", label: "Dropdown" },
  SingleChoice: { ic: "single", label: "Single Choice" },
  MultipleChoice: { ic: "multi", label: "Multiple Choice" },
  YesNo: { ic: "toggle", label: "Yes / No" },
  Severity: { ic: "chips", label: "Severity" },
  Client: { ic: "client", label: "Client(s)" },
  Staff: { ic: "staff", label: "Staff" },
  Facility: { ic: "facility", label: "Facility" },
  IssueCategory: { ic: "tag", label: "Issue Category" },
  Notifications: { ic: "notif", label: "Notifications" },
  Calculation: { ic: "calc", label: "Calculation" },
  CarePlan: { ic: "careplan", label: "Care Plan" },
};

export const PALETTE = [
  { group: "Layout", cls: "layout", items: [{ type: "section", ic: "rows", name: "Section" }] },
  {
    group: "Basic",
    cls: "",
    items: [
      { type: "Text", ic: "text", name: "Text" },
      { type: "TextArea", ic: "para", name: "Text Area" },
      { type: "Number", ic: "num", name: "Number" },
      { type: "Date", ic: "date", name: "Date" },
      { type: "Time", ic: "time", name: "Time" },
    ],
  },
  {
    group: "Choice",
    cls: "",
    items: [
      { type: "Dropdown", ic: "drop", name: "Dropdown" },
      { type: "SingleChoice", ic: "single", name: "Single Choice" },
      { type: "MultipleChoice", ic: "multi", name: "Multiple Choice" },
      { type: "YesNo", ic: "toggle", name: "Yes / No" },
      { type: "Severity", ic: "chips", name: "Severity Chips" },
    ],
  },
  {
    group: "Data",
    cls: "data",
    items: [
      { type: "Client", ic: "client", name: "Client(s)" },
      { type: "Staff", ic: "staff", name: "Staff" },
      { type: "Facility", ic: "facility", name: "Facility" },
      { type: "IssueCategory", ic: "tag", name: "Issue Category" },
      { type: "Notifications", ic: "notif", name: "Notifications" },
    ],
  },
  {
    group: "Advanced",
    cls: "adv",
    items: [
      { type: "Calculation", ic: "calc", name: "Calculation" },
      { type: "CarePlan", ic: "careplan", name: "Care Plan" },
    ],
  },
];

export const SECTION_ICONS = ["tag", "pin", "users", "doc", "cross", "shield", "notif", "check", "calc"];
export const HAS_OPTS = (t) => ["Dropdown", "SingleChoice", "MultipleChoice"].includes(t);

// The widths the builder's Width picker offers on its 12-column canvas
// (⅓ / ½ / Full) — the same three the New Request drawer lays fields out on.
export const SPANS = [4, 6, 12];

// Width a field gets when nothing was chosen for it — either because the
// author never touched the Width picker, or because the template was saved
// before `span` round-tripped through the API at all. Keeping this identical
// to what newField() assigns on drop means an untouched template renders the
// way its author saw it on the canvas.
export const defaultFieldSpan = (type) =>
  (type === "TextArea" || HAS_OPTS(type) || ["Client", "Staff", "Notifications"].includes(type) ? 12 : 6);

// Resolves a width from either shape of field — the builder's own
// ({ type, span }) or the API's ({ fieldType, span }).
export const fieldSpan = (f) =>
  (SPANS.includes(f?.span) ? f.span : defaultFieldSpan(f?.type || f?.fieldType));

let _uid = 0;
export const nid = () => "f" + Date.now().toString(36) + "_" + ++_uid;

const DEFAULT_LABEL = {
  Text: "Text Field", TextArea: "Description", Number: "Number", Date: "Date", Time: "Time",
  Dropdown: "Select One", SingleChoice: "Single Choice", MultipleChoice: "Multiple Choice",
  YesNo: "Yes / No Question", Severity: "Severity", Client: "Client(s)", Staff: "Staff",
  Facility: "Facility", IssueCategory: "Issue Category", Notifications: "Notifications",
  Calculation: "Calculated Total", CarePlan: "Use care plan?",
};

const SEVERITY_COLORS = ["#12B76A", "#F79009", "#F04438", "#D92D20", "#3538CD", "#7A5AF8", "#0BA5EC"];

export const newField = (type) => {
  const base = {
    id: nid(), type, label: DEFAULT_LABEL[type] || "Field", required: false,
    span: defaultFieldSpan(type),
    placeholder: "", help: "", options: [], conditional: null,
  };
  switch (type) {
    case "Dropdown": case "SingleChoice": case "MultipleChoice":
      base.options = ["Option 1", "Option 2"]; break;
    case "YesNo": base.yesLabel = "Yes"; base.noLabel = "No"; break;
    case "Severity":
      base.severityOptions = [
        { label: "Minor", color: SEVERITY_COLORS[0] }, { label: "Moderate", color: SEVERITY_COLORS[1] },
        { label: "Serious", color: SEVERITY_COLORS[2] }, { label: "Critical", color: SEVERITY_COLORS[3] },
      ];
      break;
    // Client/Staff are always multi-select on the submit form, matching the
    // built-in pickers — see DirectoryField in ServiceRequestFields.
    case "Client": case "Staff": base.allowMultiple = true; break;
    case "IssueCategory": base.activeOnly = true; break;
    case "Calculation": base.calc = { formula: "", type: "number" }; base.span = 6; break;
    case "CarePlan": base.carePlanScope = "clientActive"; break;
    case "Notifications": base.notifyRoles = ["admin"]; break;
    default: break;
  }
  return base;
};

export { SEVERITY_COLORS };

// Build the editable model from an existing template (or a blank one). SR
// templates never persisted `sections`, so there's no round-trip path to
// preserve here — every open derives exactly one section holding the
// template's flat fields[], same as the builder-only "Fields" section the
// previous version of this builder always used.
// Settings-step fields beyond quorum/active/approvers are builder-only
// canvas convenience — same rationale as sections/conditional above. The
// backend Template schema has no notifyAdmins[]/submitRoles/statusList
// concept, so those mirror the Studio's richer prototype UI but are not sent
// to the API. `approvers` DOES round-trip — see templatePayload below —
// restricting approve/reject votes to exactly that list once non-empty
// (serviceRequestApproval.service.js falls back to "any qualifying admin"
// while it's empty, so older templates keep working unchanged).
// Statuses available on this template's board — each is independently
// toggleable (multi-select), not a single "starting status" choice.
const DEFAULT_STATUS_LIST = () => [
  { id: "backlog", label: "Backlog", system: true, enabled: true },
  { id: "ready", label: "Ready for Work", system: true, enabled: true },
  { id: "in_review", label: "In Review", system: true, enabled: true },
  { id: "approved", label: "Approved", system: true, enabled: true },
  { id: "resolved", label: "Resolved", system: true, enabled: true },
  { id: "rejected", label: "Rejected", system: true, enabled: true },
];

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// One saved Template.fields[] entry → the builder's own field shape.
const fieldFromTemplate = (f) => ({
  id: f.fieldId || nid(),
  type: f.fieldType || "Text",
  label: f.label || "",
  placeholder: f.placeholder || "",
  help: f.helpText || "",
  // Reopening a template has to bring its layout back with it — resetting
  // every field to ½ here would silently rewrite the author's widths on
  // the next save.
  span: fieldSpan(f),
  required: !!f.required,
  options: f.options ? [...f.options] : [],
  conditional: f.conditional || null,
  severityOptions: f.severityOptions ? f.severityOptions.map((o) => ({ ...o })) : undefined,
  yesLabel: f.yesLabel, noLabel: f.noLabel,
  allowMultiple: f.allowMultiple, activeOnly: f.activeOnly,
  calc: f.formula != null || f.formulaFieldIds || f.decimalPlaces != null
    ? { formula: f.formula || "", formulaFieldIds: f.formulaFieldIds || [], decimalPlaces: f.decimalPlaces ?? 0 }
    : undefined,
  carePlanScope: f.carePlanScope,
  notifyRoles: f.notifyRoles,
});

// Regroups a saved template's flat fields[] into the section cards it was
// built in. Templates saved before sections persisted have none, and open as
// the single "Fields" section this builder has always shown them in. A field
// whose section has since been deleted lands in the first card rather than
// disappearing from the editor.
export const sectionsFromTemplate = (t) => {
  const fields = (t?.fields || []).slice().sort(byOrder);
  const defs = (t?.sections || []).slice().sort(byOrder);
  if (!defs.length) {
    return [{ id: "s" + nid(), title: "Fields", icon: "tag", cond: null, fields: fields.map(fieldFromTemplate) }];
  }
  const sections = defs.map((s) => ({
    id: s.sectionId, title: s.title || "Section", icon: s.icon || "tag", cond: s.cond || null, fields: [],
  }));
  const byId = new Map(sections.map((s) => [s.id, s]));
  fields.forEach((f) => (byId.get(f.sectionId) || sections[0]).fields.push(fieldFromTemplate(f)));
  return sections;
};

// Mirrors the backend's evaluateFormula (serviceRequestCalc.service.js) so the
// submitter watches the same number the server will store, instead of a "we'll
// work it out later" placeholder. Tokens name a field either by its fieldId or
// by the label the builder's "Insert field" picker writes — and the editor's
// hint offers × ÷ − alongside the ASCII operators, so those are accepted too.
// Returns null when the formula is empty or doesn't evaluate.
export const evaluateFormula = (formula, fields, values) => {
  if (!formula) return null;
  const labelToId = new Map(
    (fields || []).filter((f) => f.label).map((f) => [String(f.label).trim().toLowerCase(), f.fieldId])
  );
  const expr = formula
    .replace(/\{([^{}]+)\}/g, (_, token) => {
      const key = String(token).trim();
      const id = Object.prototype.hasOwnProperty.call(values || {}, key) ? key : labelToId.get(key.toLowerCase());
      const num = Number(values?.[id]);
      return Number.isFinite(num) ? String(num) : "0";
    })
    .replace(/[×✕]/g, "*")
    .replace(/[÷]/g, "/")
    .replace(/[−–—]/g, "-");
  // Whitelisted to digits, operators and parens before evaluating — the same
  // guard the builder's live preview uses.
  if (!expr.trim() || !/^[0-9.+\-*/() ]+$/.test(expr)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const n = Function('"use strict";return(' + expr + ")")();
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

// The saved template as a submit form should render it: one card per section,
// each holding the API-shape fields that belong to it, in canvas order.
// Templates saved before sections persisted come back as a single card named
// after the template — exactly what the New Request drawer showed for them
// before. Sections the author left empty are dropped: a card with no fields is
// scaffolding on the canvas, noise on the form.
export const templateFormCards = (template) => {
  const fields = (template?.fields || []).slice().sort(byOrder);
  const defs = (template?.sections || []).slice().sort(byOrder);
  if (!defs.length) {
    return fields.length ? [{ id: "fields", title: template?.name || "Details", cond: null, fields }] : [];
  }
  const cards = defs.map((s) => ({ id: s.sectionId, title: s.title || "Section", cond: s.cond || null, fields: [] }));
  const byId = new Map(cards.map((c) => [c.id, c]));
  fields.forEach((f) => (byId.get(f.sectionId) || cards[0]).fields.push(f));
  return cards.filter((c) => c.fields.length > 0);
};

export const modelFromTemplate = (t) => ({
  id: t?.id || null,
  name: t?.name || "",
  desc: t?.description || "",
  sections: sectionsFromTemplate(t),
  settings: {
    quorum: t?.defaultQuorumRequired || 1,
    active: t?.active !== false,
    approvalRequired: (t?.defaultQuorumRequired || 1) > 0,
    approvers: (t?.approvers || []).map(String),
    allStaff: t?.submitRoles?.allStaff !== false,
    submitRoles: {
      staff: t?.submitRoles?.staff !== false,
      admin: t?.submitRoles?.admin !== false,
      contractor: !!t?.submitRoles?.contractor,
    },
    notifyAdmins: [],
    statusList: DEFAULT_STATUS_LIST(),
  },
});

export const emptyModel = () => ({
  id: null,
  name: "",
  desc: "",
  sections: [{ id: "s" + nid(), title: "Fields", icon: "tag", cond: null, fields: [] }],
  settings: {
    quorum: 1, active: true,
    approvalRequired: true,
    approvers: [],
    allStaff: true,
    submitRoles: { staff: true, admin: true, contractor: false },
    notifyAdmins: [],
    statusList: DEFAULT_STATUS_LIST(),
  },
});

// Flatten sections → Template.fields[] for the API. `order`, `span` and
// `sectionId` carry the canvas layout across: between them they're what lets
// the New Request drawer rebuild the form exactly as it was designed here —
// same cards, same sequence, same widths — instead of dumping every field into
// one list and guessing a width from its type. `conditional` rides along too,
// so the drawer can hide/show fields the same way the builder's preview does.
export const fieldsFromSections = (model) => {
  let order = 0;
  return model.sections.flatMap((section) => section.fields.map((f) => {
    const entry = {
      fieldId: f.id, fieldType: f.type, label: f.label, required: !!f.required,
      helpText: f.help || undefined, placeholder: f.placeholder || undefined, order,
      span: fieldSpan(f),
      sectionId: section.id,
    };
    order += 1;
    if (HAS_OPTS(f.type)) entry.options = f.options || [];
    if (f.type === "YesNo") { entry.yesLabel = f.yesLabel; entry.noLabel = f.noLabel; }
    if (f.type === "Severity") entry.severityOptions = f.severityOptions || [];
    // Kept in the payload so the stored definition matches how the field
    // actually renders, now that both are always multi-select.
    if (["Client", "Staff"].includes(f.type)) entry.allowMultiple = true;
    if (f.type === "IssueCategory") entry.activeOnly = !!f.activeOnly;
    if (f.type === "Calculation") {
      entry.formula = f.calc?.formula || "";
      entry.formulaFieldIds = f.calc?.formulaFieldIds || [];
      entry.decimalPlaces = f.calc?.decimalPlaces ?? 0;
    }
    if (f.type === "CarePlan") entry.carePlanScope = f.carePlanScope || "clientActive";
    if (f.type === "Notifications") entry.notifyRoles = f.notifyRoles || [];
    if (f.conditional?.rules?.length) entry.conditional = f.conditional;
    return entry;
  }));
};

// The cards themselves — title, icon and their order down the canvas. The
// fields point back at these by sectionId.
export const sectionsPayload = (model) =>
  model.sections.map((s, order) => ({
    sectionId: s.id,
    title: (s.title || "").trim() || "Section",
    icon: s.icon || "tag",
    order,
    ...(s.cond?.rules?.length ? { cond: s.cond } : {}),
  }));

// The payload sent to createTemplate/updateTemplate. Template has no
// relationship to Board or Facility — a request's own facility is always
// chosen directly by the submitter (see ServiceRequestNewDrawer).
export const templatePayload = (model) => ({
  name: (model.name || "").trim(),
  description: (model.desc || "").trim() || undefined,
  fields: fieldsFromSections(model),
  sections: sectionsPayload(model),
  approvers: model.settings.approvers || [],
  submitRoles: { allStaff: model.settings.allStaff, ...model.settings.submitRoles },
  defaultQuorumRequired: model.settings.quorum,
});
